import './index.scss'
import text from './text.json'
import data from './data.json'
import Footer from '../../components/layouts/footer'
import {ResponsivePageTitle, PageSubTitle, PageTitle} from '../../components/core/title'
import Table from '../../components/core/table'
import {useEffect, useState, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {getIncidents, updateIncident, getIncidentActions, setIncidents} from '../../stores/incident-management'
import ContentLoader from '../../components/core/loader'
import EmptyContent from '../resources/EmptyContent'
import {
    PlusIcon,
    EditIcon,
    HorizontalMoreIcon,
    UserIcon,
    UsersIcon,
    LoadMoreIcon,
    ResolvedIcon,
    OpenIncidentIcon,
    AcknowledgedIcon,
    TriggeredIcon,
    InvestigatingIcon,
    IdentifiedIcon,
    MonitoringIcon,
    SearchIcon,
    FilterIcon,
    GoodNewsIcon,
    CloseIcon
} from '../../icons'
import TeamSwitcher from '../../components/pages/teams'
import moment from 'moment/moment'
import {ImgButton, SimpleBlueButton} from '../../components/core/button'
import Modal from '../../components/core/modal'
import CreateEditIncident from './CreateEditIncident'
import Dropdown from '../../components/core/dropdown'
import {MenuItem} from '@szhsin/react-menu'
import {getAllActiveMembers} from '../../stores/members'
import Customer from '../../utils/customer'
import TextHelper from '../../utils/text'
import {colors} from '../../utils/colors'
import InputWithIcon from '../../components/core/inputWithIcon'
import CustomSelect from '../../components/core/customSelect'
import DateRange from '../../components/core/dateRange'
import {addDays} from 'date-fns'
import {setRangeSelected} from '../../stores/monitoring'
import Spinner from '../../components/core/spinner'
import {getStatuspages} from '../../stores/statuspage'
import StatuspageService from '../../api/statuspage'
import useInterval from 'use-interval'
import {getTeams} from '../../stores/teams'
import classNames from 'classnames'
import {sortBySelected} from '../../utils/sorting'
import NotFound from '../resources/NotFound'
import {useLocation, useNavigate} from 'react-router-dom'
import {getResources, getServerResources} from '../../stores/resources'
import {getJobs} from '../../stores/heartbeats'
import {doNothing} from '../../utils/placeholder'

const LIVE_UPDATE_TIME = 60000

const statusOptions = Object.values(data.status_options)

const orderOptions = Object.values(data.order_options)

const resourceTypeOptions = Object.values(data.resource_options)

const statusIcons = {
    [data.status_options.resolved.value]: <ResolvedIcon />,
    [data.status_options.open.value]: <OpenIncidentIcon />,
    [data.status_options.acknowledged.value]: <AcknowledgedIcon />,
    [data.status_options.triggered.value]: <TriggeredIcon />,
    [data.status_options.investigating.value]: <InvestigatingIcon />,
    [data.status_options.identified.value]: <IdentifiedIcon />,
    [data.status_options.monitoring.value]: <MonitoringIcon />
}

const statusOptionsWithIcons = statusOptions.map(status => {
    return {
        label: <span className="d-flex align-items-center gap-half">{statusIcons[status.value]}{status.label}</span>,
        value: status.value
    }
})

const assigneeIcons = {
    team: <UsersIcon color={colors.colorPrimary} className="table-icon"/>,
    customer: <UserIcon color={colors.colorPrimary} className="table-icon"/>
}

const getIconByAssigneeType = type => assigneeIcons[type]
const SORT_ID = 'incidents'
const QUERY_PARAMS = {
    resource_type: 'resource_type',
    resource_id: 'resource_id',
    last_created_at: 'last_created_at'
}

const Incidents = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const navigate = useNavigate()
    const queryParams = new URLSearchParams(location?.search)
    const date = new Date()
    const incidents = useSelector(state => state.incident_management.incidents)
    const isFetching = useSelector(state => state.incident_management.isFetching)
    const isProcessing = useSelector(state => state.incident_management.isProcessing)
    const isLoadMoreEnabled = useSelector(state => state.incident_management.isLoadMoreEnabled)
    const buttonLoaderIsActive = useSelector(state => state.incident_management.buttonLoaderIsActive)
    const incidentActions = useSelector(state => state.incident_management.incidentActions)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const teamId = useSelector(state => state.teams.selectedTeamId)
    const teams = useSelector(state => state.teams.teams)
    const rangeSelected = useSelector(state => state.monitoring.rangeSelected)
    const statuspages = useSelector(state => state.statuspage.statuspages)
    const [isFirstLoad, setIsFirstLoad] = useState(true)
    const [selectedTab, setSelectedTab] = useState('all')
    const [filteredIncidents, setFilteredIncidents] = useState(incidents)
    const [sortedIncidents, setSortedIncidents] = useState(filteredIncidents)
    const [checkedIncidentsIds, setCheckedIncidentsIds] = useState([])
    const [incidentPopupIsActive, setIncidentPopupIsActive] = useState(false)
    const [isEdit, setIsEdit] = useState(false)
    const [allMembers, setAllMembers] = useState([])
    const [searchedText, setSearchedText] = useState('')
    const [showLoader, setShowLoader] = useState(true)
    const [queryData, setQueryData] = useState({
        resourceId: queryParams.get(QUERY_PARAMS.resource_id) || '',
        resourceType: queryParams.get(QUERY_PARAMS.resource_type) || '',
        lastCreatedAt: queryParams.get(QUERY_PARAMS.last_created_at) || '',
        queryName: ''
    })

    const [filterSettings, setFilterSettings] = useState({
        order: orderOptions[orderOptions.length - 1],
        statuses: statusOptionsWithIcons,
        range: [
            {
                startDate: date,
                endDate: addDays(date, 0),
                key: 'selection'
            }
        ],
        urgencies: data.urgency_options,
        teams: teams,
        members: allMembers,
        resourceTypes: resourceTypeOptions?.find(option => option?.type === queryData?.resourceType) || data.resource_options.all
    })

    const customFieldLogic = {
        assignees: (item) => {
            const assignees = item?.assignees
            return assignees?.[0]?.type === 'team' ?
                teams?.find(team => team?.value === assignees?.[0]?.id)?.name :
                allMembers?.find(member => member?.value === assignees?.[0]?.id)?.name
        }
    }

    const [allComponents, setAllComponents] = useState([])
    const checkedIncident = incidents?.find(item => item?.id === checkedIncidentsIds[0])

    const handleRangeChange = date => {
        const startDate = date?.[0].startDate
        const endDate = date?.[0].endDate
        const difference = moment(endDate).diff(moment(startDate), 'days')
        difference > 0 ? dispatch(setRangeSelected(true)) : dispatch(setRangeSelected(false))
        setFilterSettings({
            ...filterSettings,
            range: [
                {
                    startDate,
                    endDate,
                    key: 'selection'
                }
            ]
        })
    }

    const deleteQueryParams = (removeResourceType = true) => {
        removeResourceType && queryParams.delete(QUERY_PARAMS.resource_type)
        queryParams.delete(QUERY_PARAMS.resource_id)
        queryParams.delete(QUERY_PARAMS.last_created_at)
        navigate(`${location.pathname}?${queryParams.toString()}`)
    }

    const resetFilters = () => {
        setShowLoader(false)
        dropdownMenuRef?.current?.closeMenu()
        setFilterSettings({
            order: orderOptions[orderOptions.length - 1],
            statuses: statusOptionsWithIcons,
            range: [
                {
                    startDate: date,
                    endDate: addDays(date, 0),
                    key: 'selection'
                }
            ],
            urgencies: data.urgency_options,
            teams: teams,
            members: allMembers,
            resourceTypes: resourceTypeOptions
        })
        deleteQueryParams()
        setQueryData({
            resourceId: '',
            resourceType: '',
            lastCreatedAt: '',
            queryName: ''
        })
        setFilteredIncidents(incidents)
    }

    const applyFilters = async() => {
        setShowLoader(false)
        setQueryData({
            resourceId: '',
            resourceType: filterSettings.resourceTypes?.type,
            queryName: '',
            lastCreatedAt: ''
        })
        navigate(`${location.pathname}?${queryParams.toString()}`)
        dropdownMenuRef?.current?.closeMenu()
        let filterData = incidents ? [...incidents] : []
        const statuses = filterSettings.statuses ? filterSettings.statuses.map(s => s.value) : []
        const urgencies = filterSettings.urgencies ? filterSettings.urgencies.map(u => u.value) : []
        const dateRange = filterSettings.range ? filterSettings.range[0] : null
        const isRangeSelected = dateRange.startDate.toISOString() !== dateRange.endDate.toISOString()
        const hasTeamFilter = filterSettings.teams.length > 0
        const hasMemberFilter = filterSettings.members.length > 0
        const assignees = filterSettings.teams.concat(filterSettings.members)?.map(a => a.value)

        if (statuses.length > 0) {
            filterData = filterData.filter((item) => statuses.includes(item.status))
        }

        if (urgencies.length > 0) {
            filterData = filterData.filter((item) => urgencies.includes(item.urgency))
        }

        if (filterSettings?.resourceTypes?.length > 0) {
            filterData = filterSettings.resourceTypes?.some(resourceType => resourceType.value === 'all') ? filterData :
                filterData.filter(item =>
                    filterSettings.resourceTypes.some(resourceType => resourceType?.trigger_type === item?.trigger_source?.split('#')[0])
                )
        }

        if (dateRange && isRangeSelected) {
            const startDate = new Date(dateRange.startDate)
            const endDate = new Date(dateRange.endDate)

            filterData = filterData.filter((item) => {
                const updatedAt = new Date(item.updated_at)
                return updatedAt >= startDate && updatedAt <= endDate
            })
        }

        if (hasTeamFilter || hasMemberFilter) {
            filterData = filterData.filter(item => {
                return item?.assignees?.some(assignee => assignees.includes(assignee.id))
            })
        }
        setFilteredIncidents(filterData)
    }

    const getFilterButtons = () => {
        return <div className="buttons w-100 d-flex gap-1 mt-1">
            <SimpleBlueButton onClick={resetFilters} outline={true}>{text.reset}</SimpleBlueButton>
            <SimpleBlueButton onClick={applyFilters}>{text.apply}</SimpleBlueButton>
        </div>
    }

    const dropdownMenuRef = useRef()

    const tableHeader = [
        {
            label: 'Urgency',
            className: 'min-width-150',
            sorting: {
                asc: {
                    key: 'urgency_asc',
                    field: ['urgency']
                },
                desc: {
                    key: 'urgency_desc',
                    field: ['urgency']
                }
            }
        },
        {
            label: 'Name',
            className: 'min-width-250',
            sorting: {
                asc: {
                    key: 'name_asc',
                    field: ['name']
                },
                desc: {
                    key: 'name_desc',
                    field: ['name']
                }
            }
        },
        {
            label: 'Description',
            className: 'min-width-400',
            sorting: {
                asc: {
                    key: 'description_asc',
                    field: ['description']
                },
                desc: {
                    key: 'description_desc',
                    field: ['description']
                }
            }
        },
        {
            label: 'Status',
            className: 'min-width-150',
            sorting: {
                asc: {
                    key: 'status_asc',
                    field: ['status']
                },
                desc: {
                    key: 'status_desc',
                    field: ['status']
                }
            }
        },
        {
            label: 'Assignees',
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'assignees_asc',
                    field: ['assignees']
                },
                desc: {
                    key: 'assignees_desc',
                    field: ['assignees']
                }
            }
        },
        {
            label: 'Created at',
            className: 'min-width-150',
            sorting: {
                asc: {
                    key: 'created_at_asc',
                    field: ['created_at']
                },
                desc: {
                    key: 'created_at_desc',
                    field: ['created_at']
                }
            }
        },
        {
            label: 'Updated at',
            className: 'flex-1 justify-content-flex-start gap-12',
            sorting: {
                asc: {
                    key: 'updated_at_asc',
                    field: ['updated_at']
                },
                desc: {
                    key: 'updated_at_desc',
                    field: ['updated_at']
                }
            }
        }
    ]

    const generateTriggers = id => {
        return incidents?.find(item => item?.id === id)?.trigger_type === data.trigger_types.manual.value &&
        incidents?.find(item => item?.id === id)?.status !== data.status_options.resolved.value &&
        <Dropdown menuButton={<button className={classNames('table-action-btn', {
            dark: isDarkMode
        })}><HorizontalMoreIcon/></button>}>
            <MenuItem onClick={() => {
                handleButtonOnclick()
                setIsEdit(true)
                setCheckedIncidentsIds([id])
            }}>
                <span className="d-flex"><EditIcon/></span>
                <span>{text.edit}</span>
            </MenuItem>
        </Dropdown>
    }

    const changeTab = key => {
        setSelectedTab(key)
        setCheckedIncidentsIds([])
        setSearchedText('')
    }

    const generateTabMenu = () => {
        return (
            <div className={classNames('sections', {
                dark: isDarkMode
            })}>
                {
                    [
                        {
                            label: 'All incidents',
                            value: 'all'
                        }
                    ]?.concat(statusOptions).map((item, index) => (
                        <span onClick={() => changeTab(item.value)}
                            className={classNames('section-text', {
                                active: item.value === selectedTab,
                                dark: isDarkMode
                            })}
                            key={index}>{item.label}</span>
                    ))}
            </div>
        )
    }

    const getAllMembers = async() => {
        setAllMembers(await dispatch(getAllActiveMembers(teams)))
    }

    const getAssignees = assignees => {
        const remainingAssignees = assignees?.length > 1 ?
            `, +${assignees?.length - 1} more` : ''
        return (
            <>
                {getIconByAssigneeType(assignees?.[0]?.type)}
                <span className="d-flex">
                    {
                        assignees?.[0]?.type === 'team' ?
                            <span>{`${teams?.find(team => team?.value === assignees?.[0]?.id)?.name}`}</span> :
                            <span>{`${allMembers.find(member => member?.value === assignees?.[0]?.id)?.name}`}</span>
                    }
                    <Dropdown
                        menuButton={<span className="more">{remainingAssignees}</span>}
                        align='start'
                    >
                        {
                            assignees?.slice(1)?.map((assignee, index) => {
                                return (
                                    <MenuItem
                                        onClick={e => e.preventDefault()}
                                        key={index + assignee?.id}
                                    >
                                        <span className="d-flex align-items-center gap-half">
                                            {getIconByAssigneeType(assignee?.type)}
                                            {assignee?.type === 'team' ?
                                                teams?.find(team => team?.value === assignee?.id)?.name :
                                                `${allMembers.find(member => member?.value === assignee?.id)?.name} ${allMembers.find(member => member?.value === assignee?.id)?.surname}`
                                            }
                                        </span>
                                    </MenuItem>
                                )
                            })
                        }
                    </Dropdown>
                </span>
            </>
        )
    }

    const tbodyData = sortedIncidents?.map(item => {
        const createdAt = moment(item?.created_at).format('YYYY-MM-DD HH:mm')
        const updatedAt = moment(item?.updated_at).format('YYYY-MM-DD HH:mm')
        return [
            <span className="min-width-150">
                <span className={classNames(`badge urgency ${item?.urgency || text.monitoring} capitalize`, {
                    dark: isDarkMode
                })}>{item?.urgency}</span>
            </span>,
            <span className="min-width-250" title={item?.name}>
                {TextHelper.textLengthChecker(item?.name, 60) || '-'}
            </span>,
            <span className="min-width-400" title={item?.description}>
                {TextHelper.textLengthChecker(item?.description, 60) || '-'}
            </span>,
            <span className="min-width-150">
                {
                    (isProcessing && item?.id === checkedIncident?.id) ?
                        <Spinner classname='ml-0' /> :
                        <span className={classNames(`badge status ${item?.status || text.monitoring} capitalize`, {
                            dark: isDarkMode
                        })}>{item?.status}</span>
                }
            </span>,
            <span className="min-width">
                {(item?.assignees?.length && getAssignees(item?.assignees)) || text.unassigneed}
            </span>,
            <span className="min-width-150">
                <span>{createdAt}</span>
            </span>,
            <span className="flex-1">
                <span>{updatedAt === createdAt ? text.not_available : updatedAt}</span>
                <span>{generateTriggers(item?.id)}</span>
            </span>
        ]
    })

    const getActionButtonStatus = () => {
        const triggerType = checkedIncident?.trigger_type
        return checkedIncidentsIds?.length !== 1 ||
            (triggerType !== data.trigger_types.manual.value && triggerType !== data.trigger_types.webhook.value) ||
            checkedIncident?.status === data.status_options.resolved.value
    }

    const searchIncident = e => {
        setSearchedText(e.target.value.toLowerCase())
    }

    const handleStatusUpdate = value => {
        const components = incidentActions?.length > 0 ?
            incidentActions?.filter(action => action?.type === data?.action_types_options?.trigger_statuspage_update)?.map(action => {
                return action?.settings?.map(setting => {
                    return allComponents?.find(component => component?.value === setting?.target?.id)
                })
            })?.reduce((accumulator, currentArray) => {
                return accumulator.concat(currentArray)
            }, []) : []
        const actionTypes = incidentActions?.length > 0 ? incidentActions?.map(action => action?.type) : []
        dispatch(updateIncident(
            checkedIncident?.id,
            {
                name: checkedIncident?.name,
                description: checkedIncident?.description,
                urgency: checkedIncident?.urgency,
                actions: actionTypes?.map(type => {
                    return type === data?.action_types_options?.trigger_alerts ?
                        {
                            type: type
                        } :
                        {
                            type: type,
                            settings: components?.map(component => {
                                return {
                                    status_page_id: component?.statuspage_id,
                                    component_id: component?.value
                                }
                            })
                        }
                }),
                assignees: checkedIncident?.assignees,
                status: value.value
            },
            setCheckedIncidentsIds,
            teamId
        ))
    }

    const handleResourceTypeChange = data => {
        if (data.type !== queryData.resourceType) {
            deleteQueryParams()
        }
        setFilterSettings({
            ...filterSettings,
            resourceTypes: data
        })
    }

    const generateIncidents = () => {
        return isFetching ? <ContentLoader /> :
            <>
                <div className="search-filter-actions">
                    <div className={classNames('search-actions', {
                        dark: isDarkMode
                    })}>
                        <InputWithIcon placeholder={text.search}
                            icon={<SearchIcon/>}
                            onChange={searchIncident}
                            value={searchedText}
                            type="text"/>
                        <Dropdown closeMenu={true}
                            menuButton={
                                <button className={classNames('filter-button', {
                                    active: queryData.resourceType
                                })}>
                                    <FilterIcon/>
                                    {text.filters}
                                </button>
                            } innerRef={dropdownMenuRef}>
                            <div className={classNames('incident-filter', {
                                dark: isDarkMode
                            })}>
                                <div className={classNames('selects', {
                                    dark: isDarkMode
                                })}>
                                    <div className="status">
                                        <span className="label">{text.status}</span>
                                        <CustomSelect
                                            options={statusOptionsWithIcons}
                                            isMulti
                                            value={filterSettings.statuses}
                                            placeholder={text.choose_status}
                                            onChange={(_, value) => setFilterSettings({
                                                ...filterSettings,
                                                statuses: value
                                            })}
                                        />
                                    </div>
                                    <div className="date">
                                        <span className="label">{text.updated}</span>
                                        <DateRange
                                            placeholder={rangeSelected ? text.range_selected : text.choose_date}
                                            state={filterSettings.range}
                                            onChange={handleRangeChange}
                                        />
                                    </div>
                                    <div className="urgency">
                                        <span className="label">{text.urgency}</span>
                                        <CustomSelect
                                            placeholder={text.choose_urgency}
                                            isMulti
                                            options={data.urgency_options}
                                            value={filterSettings.urgencies}
                                            onChange={(_, value) => setFilterSettings({
                                                ...filterSettings,
                                                urgencies: value
                                            })}
                                        />
                                    </div>
                                    <div className="team">
                                        <span className="label">{text.assignees}</span>
                                        <CustomSelect
                                            placeholder={text.choose_team}
                                            isMulti
                                            options={teams}
                                            value={filterSettings.teams}
                                            onChange={(_, value) => setFilterSettings({
                                                ...filterSettings,
                                                teams: value
                                            })}
                                        />
                                    </div>
                                    <div className="member">
                                        <span className="label"></span>
                                        <CustomSelect
                                            placeholder={text.choose_member}
                                            isMulti
                                            options={allMembers}
                                            value={filterSettings.members}
                                            onChange={(_, value) => setFilterSettings({
                                                ...filterSettings,
                                                members: value
                                            })}
                                        />
                                    </div>
                                    <div className="resource-type" id="resource-type">
                                        <span className="label">{text.resource_type}</span>
                                        <div className='resource-name-field'>
                                            <CustomSelect
                                                placeholder={text.resource_type}
                                                options={resourceTypeOptions}
                                                blurInputOnSelect
                                                value={filterSettings.resourceTypes}
                                                onChange={(_, data) => handleResourceTypeChange(data)}
                                            />
                                            {queryData.queryName && <span className={classNames('resource-name-label', {
                                                dark: isDarkMode
                                            })} title={queryData.queryName}>
                                                {TextHelper.textLengthChecker(queryData.queryName, 20)}
                                                <span onClick={clearResourceName} className='clear-icon'><CloseIcon/></span>
                                            </span>}
                                        </div>
                                    </div>
                                    {getFilterButtons()}
                                </div>
                            </div>
                        </Dropdown>
                    </div>
                    <div className={classNames('selected-actions', {
                        dark: isDarkMode
                    })}>
                        <div className={`status-update ${getActionButtonStatus() ? 'cursor-not-allowed' : ''}`}>
                            <CustomSelect
                                options={statusOptionsWithIcons?.filter(item => checkedIncident?.status !== item?.value)}
                                placeholder={text.choose_status}
                                isDisabled={getActionButtonStatus()}
                                value={null}
                                onChange={(_, value) => handleStatusUpdate(value)}
                            />
                        </div>
                        <ImgButton className={classNames('responsive-add-button', {
                            'light-bg': !isDarkMode
                        })}
                        onClick={() => {
                            setIsEdit(false)
                            handleButtonOnclick()
                        }}
                        >
                            <PlusIcon stroke={colors.colorMuted}/>
                            <span className="button-text">{text.new}</span>
                        </ImgButton>
                        <ImgButton className={classNames({
                            'light-bg': !isDarkMode
                        })}
                        disabled={getActionButtonStatus()}
                        onClick={() => {
                            setIsEdit(true)
                            handleButtonOnclick()
                        }}
                        >
                            <EditIcon/>
                            <span className="button-text">{text.edit}</span>
                        </ImgButton>
                    </div>
                </div>
                {
                    !incidents?.length ?
                        <NotFound message={text.not_found_incidents} icon={<GoodNewsIcon className='good-news-icon'/>}/> :
                        sortedIncidents?.length > 0 ?
                            <>
                                <div  className={classNames('content-body', {
                                    dark: isDarkMode
                                })}>
                                    <Table
                                        tableHeader={tableHeader}
                                        tbodyData={tbodyData}
                                        data={sortedIncidents}
                                        identificator='id'
                                        inputName='id'
                                        selectedData={checkedIncidentsIds}
                                        isMultiCheckable={false}
                                        hasCheckbox={true}
                                        setSelectedData={data => setCheckedIncidentsIds(data)}
                                        isSortingEnabled
                                        setData={setSortedIncidents}
                                        filteredData={filteredIncidents}
                                        sortId={SORT_ID}
                                        customFieldLogic={customFieldLogic}
                                    />
                                </div>
                                {isLoadMoreEnabled &&
                            <ImgButton
                                onClick={loadMoreIncidents}
                                type="button"
                                className={`load-button ${buttonLoaderIsActive ? 'active' : ''}`}
                            >
                                <LoadMoreIcon stroke={colors.colorMuted} />
                                {text.load_more}
                            </ImgButton>}
                            </> : <EmptyContent
                                message={text.empty_content_text}
                                hideButton={true}
                            />
                }
            </>
    }

    const handleButtonOnclick = () => {
        setIncidentPopupIsActive(!incidentPopupIsActive)
    }

    const loadMoreIncidents = () => {
        resetFilters()
        const lastCreatedAt = incidents?.reduce((prev, current) => {
            const prevDate = new Date(prev?.created_at)
            const currentDate = new Date(current?.created_at)
            return prevDate < currentDate ? prev : current
        })?.created_at
        dispatch(getIncidents(teamId, lastCreatedAt, false, true))
        setSelectedTab('all')
        setSearchedText('')
    }

    const getStatuspagesComponents = async() => {
        try {
            const promises = statuspages?.map(statuspage => {
                return StatuspageService.getStatuspageComponents(statuspage?.id)
            })
            const res = await Promise.all(promises)
            const components = res?.map((item, index) => {
                const data = item?.data?.data?.components?.filter(item => item?.type === 'custom')
                return data?.map(item => {
                    return {
                        label: `${item?.name} (${statuspages?.[index]?.name})`,
                        value: item?.id,
                        statuspage_id: statuspages?.[index]?.id
                    }
                })
            }).filter(item => item?.length > 0)
            return components?.reduce((accumulator, currentArray) => {
                return accumulator.concat(currentArray)
            }, [])
        } catch (e) {
            doNothing()
        }
    }

    const getAllComponents = async() => {
        setAllComponents(await getStatuspagesComponents())
    }

    const clearResourceName = () => {
        deleteQueryParams(false)
        setShowLoader(false)
        setQueryData({...queryData, queryName: ''})
    }

    const getQueryResources = async() => {
        let resourceData = []

        if (queryData.resourceType === data.resource_options.resources.type) {
            resourceData = await dispatch(getResources(true, true))
            resourceData = resourceData.map(item => ({...item, id: item?.resource_id}))
        }
        if (queryData.resourceType === data.resource_options.server_resources.type) {
            resourceData = await dispatch(getServerResources(false, true))
        }
        if (queryData.resourceType === data.resource_options.heartbeats.type) {
            resourceData = await dispatch(getJobs())
            resourceData = resourceData.map(item => ({...item, id: item?.heartbeat_id}))
        }
        setQueryData({...queryData, queryName: resourceData?.find(resource => resource?.id === queryData.resourceId)?.name})
        return resourceData
    }

    useEffect(() => {
        dispatch(setIncidents({data: []}))
        !Customer.isRoot() && dispatch(getTeams())
    }, [])

    const getAsyncIncidents = async() => {
        await dispatch(getIncidents(teamId, '', showLoader, true, queryData.resourceType, queryData.resourceId, true))
        return true
    }

    useEffect(() => {
        const fetchData = async() => {
            if (queryParams.get(QUERY_PARAMS.resource_id)) {
                getQueryResources()
            }

            setFilterSettings({
                ...filterSettings,
                resourceTypes: resourceTypeOptions.find(resourceType => resourceType.type === queryData.resourceType)
            })

            const success = await getAsyncIncidents()
            if (success) {
                setIsFirstLoad(false)
            }
        }

        fetchData()
    }, [queryData.resourceType])

    useEffect(() => {
        if (queryData.resourceType && !isFirstLoad) {
            dispatch(
                getIncidents(
                    teamId,
                    queryData.lastCreatedAt,
                    false,
                    true,
                    queryData.resourceType,
                    queryData.resourceId
                )
            )
        }
    }, [queryData.lastCreatedAt, isFirstLoad])

    useEffect(() => {
        selectedTab === 'all' ?
            setFilteredIncidents(incidents) :
            setFilteredIncidents(incidents?.filter(item => item?.status?.includes(selectedTab)))
    }, [selectedTab])

    useEffect(() => {
        setFilteredIncidents(incidents)
        setSortedIncidents(incidents)
    }, [incidents])

    useEffect(() => {
        dispatch(getStatuspages())
        setSelectedTab('all')

        if (!queryParams.get(QUERY_PARAMS.resource_type)) {
            dispatch(getIncidents(teamId, '', showLoader))
            return
        }
    }, [teamId])

    useEffect(() => {
        getAllMembers()
    }, [teams])

    useEffect(() => {
        setFilteredIncidents(incidents?.filter(incident => incident?.name?.toLowerCase().includes(searchedText) || incident?.description?.toLowerCase().includes(searchedText)))
    }, [searchedText])

    useEffect(() => {
        checkedIncident?.id && dispatch(getIncidentActions(checkedIncident?.id))
    }, [checkedIncident])

    useEffect(() => {
        if (statuspages?.length > 0) {
            getAllComponents()
        }
    }, [statuspages])

    useEffect(() => {
        setSortedIncidents(sortBySelected(filteredIncidents, SORT_ID, tableHeader, customFieldLogic))
    }, [filteredIncidents, teams, allMembers])

    const getIncidentsInterval = async() => {
        const newIncidents = await dispatch(getIncidents(teamId, '', false, false, queryData.resourceType, queryData.resourceId))
        return newIncidents
    }

    useInterval(async() => {
        const newIncidents = await getIncidentsInterval()
        const updatedIncidents = [
            ...newIncidents,
            ...incidents.slice(newIncidents.length)
        ]?.filter(data => !!data)
        dispatch(setIncidents({data: updatedIncidents}))
    }, LIVE_UPDATE_TIME)

    return (
        <main className={classNames('main incidents', {
            dark: isDarkMode
        })}>
            <Modal contentClassName="overflow-auto" title={isEdit ? text.edit_incident : text.add_incident} closeModal={handleButtonOnclick}
                isOpen={incidentPopupIsActive}>
                <CreateEditIncident
                    isEdit={isEdit}
                    incident={incidents?.find(item => item?.id === checkedIncidentsIds?.[0])}
                    closeModal={handleButtonOnclick}
                    setCheckedIncidentsIds={setCheckedIncidentsIds}
                    setSelectedTab={setSelectedTab}
                    allMembers={allMembers}
                    allComponents={allComponents}
                />
            </Modal>
            <div>
                <ResponsivePageTitle/>
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <PageSubTitle>
                                <p className={classNames('section', {
                                    dark: isDarkMode
                                })}>{text.section}</p>
                            </PageSubTitle>
                            <PageTitle>
                                <h1  className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>
                    <ImgButton
                        onClick={async() => {
                            await setIsEdit(false)
                            handleButtonOnclick()
                        }}
                    >
                        {text.add_new_incident}
                        <span><PlusIcon/></span>
                    </ImgButton>
                </div>
                <div>
                    {Customer.isRoot() && (
                        <TeamSwitcher/>
                    )}
                    {generateTabMenu()}
                </div>
                {generateIncidents()}
            </div>
            <Footer/>
        </main>
    )
}

export default Incidents
